import { usePageTitle } from "@/hooks/usePageTitle";
import { useState } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, QrCode, FileText, MapPin, LineChart, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const NT_IMG = "https://images.pexels.com/photos/15391542/pexels-photo-15391542.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const tools = [
  { icon: LineChart, title: "Live earnings dashboard", text: "Real-time revenue from your QR-attributed sign-ups, with monthly trends." },
  { icon: FileText, title: "Automated statements", text: "Monthly summary with deductions for equipment lease & arrangement fees calculated for you." },
  { icon: MapPin, title: "Venue management", text: "Organise the local venues where you run your in-person classes." },
  { icon: QrCode, title: "Personal QR & referral link", text: "Share one code — every sign-up is automatically attributed to you." },
  { icon: Wallet, title: "Keep 80% of every fee", text: "You retain 80% of client fees. Just a £TBC fixed monthly lease to MBBC." },
];

const scrollToForm = () => document.getElementById("interest-form")?.scrollIntoView({ behavior: "smooth" });

function InterestForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", qualifications: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/therapist-interest", form);
      setSent(true);
      toast.success("Thank you — your interest has been sent to Andrea!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="max-w-xl mx-auto text-center bg-white border border-[#E7E5E4] rounded-2xl p-10 shadow-sm" data-testid="interest-success">
        <CheckCircle2 className="w-14 h-14 text-teal mx-auto" />
        <h3 className="mt-5 font-heading text-2xl text-navy">Thank you for your interest!</h3>
        <p className="mt-3 text-[#57534E] leading-relaxed">
          Your details have been sent to Andrea. She'll be in touch personally to discuss the next steps
          in becoming a My Best Body Club™ Nutritional Therapist.
        </p>
      </div>
    );
  }

  const cls = "w-full bg-white border border-[#E7E5E4] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal";
  return (
    <form onSubmit={submit} className="max-w-xl mx-auto bg-white border border-[#E7E5E4] rounded-2xl p-8 shadow-sm space-y-4" data-testid="interest-form-card">
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="interest-name" required placeholder="Full name *" value={form.name} onChange={set("name")} className={cls} />
        <input data-testid="interest-email" type="email" required placeholder="Email *" value={form.email} onChange={set("email")} className={cls} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="interest-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={cls} />
        <input data-testid="interest-location" placeholder="Location / Area" value={form.location} onChange={set("location")} className={cls} />
      </div>
      <textarea data-testid="interest-qualifications" rows={3} placeholder="Your qualifications & background" value={form.qualifications} onChange={set("qualifications")} className={cls} />
      <textarea data-testid="interest-message" rows={3} placeholder="Anything else you'd like Andrea to know?" value={form.message} onChange={set("message")} className={cls} />
      <button data-testid="interest-submit" type="submit" disabled={busy}
        className="w-full bg-teal text-white hover:bg-teal-dark transition-colors rounded-full py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Register my interest
      </button>
      <p className="text-xs text-[#78716C] text-center">Your enquiry goes directly to Andrea, who will be in touch personally.</p>
    </form>
  );
}

export default function BecomeNT() {
  usePageTitle("For Nutritional Therapists");
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative overflow-hidden">
        <div className="brand-blob bg-teal" style={{ width: 420, height: 420, top: -100, left: -80 }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">For Nutritional Therapists</span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl tracking-tight leading-[1.08] text-navy">
              Build a thriving local practice — we handle the rest.
            </h1>
            <p className="mt-5 text-lg text-[#57534E] max-w-lg leading-relaxed">
              Join My Best Body Club to deliver in-person, data-driven nutrition classes in your area.
              Keep <strong className="text-navy">80%</strong> of every client fee and run everything from one simple dashboard.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <button onClick={scrollToForm} data-testid="nt-register-cta"
                className="inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark transition-colors rounded-full px-8 py-3.5 font-medium">
                Register your interest <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
            className="rounded-[2rem] overflow-hidden shadow-2xl">
            <img src={NT_IMG} alt="Nutritional Therapist" className="w-full h-[440px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Economics */}
      <section className="py-20 px-6 lg:px-8 bg-navy text-white">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-8 text-center">
          {[["80%", "of client fees are yours"], ["20%", "supports the MBBC platform"], ["£TBC", "fixed monthly equipment lease"]].map(([n, l]) => (
            <div key={l}>
              <div className="font-heading text-5xl text-teal">{n}</div>
              <p className="mt-2 text-white/70 text-sm">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Tools */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-navy">Everything you need to run your practice</h2>
            <p className="mt-4 text-[#57534E]">Your therapist dashboard puts performance, earnings and clients in one place.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tools.map((t, i) => (
              <motion.div key={t.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="bg-white border border-[#E7E5E4] rounded-2xl p-7 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center text-teal"><t.icon className="w-6 h-6" /></div>
                <h3 className="mt-5 font-heading text-xl text-navy">{t.title}</h3>
                <p className="mt-2 text-sm text-[#57534E] leading-relaxed">{t.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6 lg:px-8 bg-[#F5F5F4]">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl text-navy text-center">How it works</h2>
          <div className="mt-10 space-y-5">
            {[
              "Register your interest below — Andrea will be in touch to discuss the next steps.",
              "Once approved, get your unique QR code, referral link and therapist dashboard.",
              "Set up your local venues and start running in-person BIA classes.",
              "Track earnings live and receive an automated monthly financial statement.",
            ].map((s, i) => (
              <div key={s} className="flex items-start gap-4 bg-white border border-[#E7E5E4] rounded-xl p-5">
                <div className="w-8 h-8 rounded-full bg-navy text-white flex items-center justify-center font-heading text-sm shrink-0">{i + 1}</div>
                <p className="text-[#44403C]">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interest form */}
      <section id="interest-form" className="py-24 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-teal">Take the first step</span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight text-navy">Register your interest</h2>
          <p className="mt-4 text-[#57534E]">Complete the form below and Andrea will contact you personally to discuss becoming a MBBC Nutritional Therapist.</p>
        </div>
        <InterestForm />
      </section>

      <Footer />
    </div>
  );
}
